import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Tooltip,
} from "@mui/material";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { gray } from "../../../../theme/themePrimitives";
import { ColId, Order } from "../Users";
import {
  GetUserResult,
  UserPortalMapping,
  UserStatusType,
} from "../../../../services/api/types/users/users";
import { Link } from "react-router-dom";
import { ReactComponent as HubIcon } from "../../../../assets/hub.svg";
import { ReactComponent as PortalIcon } from "../../../../assets/portal.svg";

const PENDING_STATUSES = new Set<string>([
  UserStatusType.INVITATION_PENDING.toLowerCase(),
  UserStatusType.ARCHIVED_PENDING.toLowerCase(),
  UserStatusType.EMAIL_CHANGE_PENDING.toLowerCase(),
]);

const isPending = (user: GetUserResult) =>
  PENDING_STATUSES.has((user.status ?? "").toLowerCase());

const TOOLTIP_WIDTH_PX = 360;

const PortalChip = ({
  label,
  showIcon = true,
}: {
  label: string;
  showIcon?: boolean;
}) => {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        px: 1,
        py: 0.5,
        borderRadius: "999px",
        backgroundColor: "rgba(255, 255, 255, 0.35)",
        border: "1px solid #BDBDBD",
        minHeight: 32,
        whiteSpace: "nowrap",
      }}
    >
      {showIcon ? (
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: "999px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid #BDBDBD",
            backgroundColor: "#BDBDBD",
          }}
        >
          <PortalIcon width={20} height={20} />
        </Box>
      ) : null}
      <Typography
        sx={{
          fontSize: 14,
          lineHeight: "20px",
          fontWeight: 500,
          color: "white",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const MultiPortalTooltipContent = ({
  portals,
}: {
  portals: UserPortalMapping[];
}) => {
  const [open, setOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState<number>(() =>
    Math.min(2, portals.length),
  );

  const chipsMeasureRowRef = useRef<HTMLDivElement | null>(null);
  const overflowMeasureRef = useRef<HTMLDivElement | null>(null);

  const portalNames = useMemo(
    () => portals.map((p) => p.name).filter(Boolean),
    [portals],
  );

  const overflowLabel = `+${Math.max(0, portalNames.length - visibleCount)}`;

  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const rowEl = chipsMeasureRowRef.current;
      if (!rowEl) return;

      const children = Array.from(rowEl.children) as HTMLElement[];
      if (children.length === 0) return;

      const available = TOOLTIP_WIDTH_PX - 16 * 2;
      const gap = 8;
      const widths = children.map((el) => el.offsetWidth);

      const overflowWidth = overflowMeasureRef.current?.offsetWidth ?? 0;

      let best = 0;
      for (let k = 0; k <= widths.length; k += 1) {
        const shown = k;
        const hidden = widths.length - shown;
        const shownWidth =
          widths.slice(0, k).reduce((sum, w) => sum + w, 0) +
          (shown > 1 ? gap * (shown - 1) : 0);
        const needsOverflow = hidden > 0;
        const total =
          shownWidth +
          (needsOverflow && shown > 0 ? gap : 0) +
          (needsOverflow ? overflowWidth : 0);

        if (total <= available) {
          best = k;
        } else {
          break;
        }
      }

      const clamped = Math.max(1, Math.min(best, widths.length));
      setVisibleCount(clamped);
    };

    const raf = window.requestAnimationFrame(measure);
    return () => window.cancelAnimationFrame(raf);
  }, [open, portalNames.length, overflowLabel]);

  const shownPortals = portalNames.slice(0, visibleCount);
  const shownRemaining = Math.max(0, portalNames.length - shownPortals.length);

  return (
    <Tooltip
      placement="top-start"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [40, -15],
              },
            },
          ],
        },
        tooltip: {
          sx: {
            width: TOOLTIP_WIDTH_PX,
            maxWidth: TOOLTIP_WIDTH_PX,
            backgroundColor: gray[800],
            border: "1px solid #BDBDBD",
            borderRadius: "16px",
            px: 2,
            py: 1.5,
          },
        },
        arrow: {
          sx: {
            color: gray[800],
            "&::before": { border: "1px solid #BDBDBD" },
          },
        },
      }}
      title={
        <Box>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <HubIcon width={20} height={20} />
            <Typography sx={{ fontWeight: 700, fontSize: 20, color: "white" }}>
              Multi portal user
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 16, color: "white" }}>
            This user has been assigned to multiple portals, each with a
            specific role.
          </Typography>
          <Typography sx={{ mt: 1.5, fontWeight: 700, fontSize: 18 }}>
            Assigned Portals
          </Typography>

          <Box
            sx={{
              mt: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "nowrap",
              overflow: "hidden",
            }}
          >
            {shownPortals.map((name) => (
              <PortalChip key={name} label={name} />
            ))}
            {shownRemaining > 0 ? (
              <PortalChip label={`+${shownRemaining}`} showIcon={false} />
            ) : null}
          </Box>
          <Box
            sx={{
              position: "absolute",
              visibility: "hidden",
              height: 0,
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            <Box
              ref={chipsMeasureRowRef}
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              {portalNames.map((name) => (
                <PortalChip key={`measure-${name}`} label={name} />
              ))}
            </Box>
            <Box ref={overflowMeasureRef} sx={{ display: "inline-flex" }}>
              <PortalChip label={`+${portalNames.length}`} showIcon={false} />
            </Box>
          </Box>
        </Box>
      }
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: 24,
          height: 24,
          borderRadius: "4px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          border: "0.5px solid #9E77ED",
          backgroundColor: "#F4EBFF",
        }}
      >
        <HubIcon width={16} height={16} />
      </Box>
    </Tooltip>
  );
};

type UsersTableProps = {
  users: GetUserResult[];
  selectedUsers: GetUserResult[];
  onSelectionChange: (users: GetUserResult[]) => void;
  sortOrder: Order;
  orderBy: ColId;
  linkTo: string;
  onToggleSort: (col: ColId) => void;
  isFiltered?: boolean;
};

export const UsersTable = ({
  users,
  selectedUsers,
  onSelectionChange,
  sortOrder,
  orderBy,
  linkTo,
  onToggleSort,
  isFiltered = false,
}: UsersTableProps) => {
  const columns: { id: ColId; label: string }[] = [
    { id: "email", label: "Email" },
    { id: "firstName", label: "First name" },
    { id: "lastName", label: "Last name" },
    { id: "status", label: "Status" },
  ];

  const isSelected = (user: GetUserResult) =>
    selectedUsers.some(
      (selectedUser) => selectedUser.UserPortalUid === user.UserPortalUid,
    );

  const selectableUsers = users.filter((user) => !isPending(user));

  const allSelected =
    selectableUsers.length > 0 &&
    selectableUsers.every((user) => isSelected(user));

  const someSelected =
    selectableUsers.some((user) => isSelected(user)) && !allSelected;

  const toggleRow = (user: GetUserResult) => {
    if (isPending(user)) return;

    if (isSelected(user)) {
      onSelectionChange(
        selectedUsers.filter(
          (selectedUser) => selectedUser.UserPortalUid !== user.UserPortalUid,
        ),
      );
      return;
    }

    onSelectionChange([...selectedUsers, user]);
  };

  const toggleAll = () => {
    const currentPageIds = new Set(
      selectableUsers.map((user) => user.UserPortalUid),
    );

    if (allSelected) {
      onSelectionChange(
        selectedUsers.filter(
          (selectedUser) => !currentPageIds.has(selectedUser.UserPortalUid),
        ),
      );
      return;
    }

    const merged = new Map(
      selectedUsers.map((selectedUser) => [
        selectedUser.UserPortalUid,
        selectedUser,
      ]),
    );

    selectableUsers.forEach((user) => {
      if (!merged.has(user.UserPortalUid)) {
        merged.set(user.UserPortalUid, user);
      }
    });

    onSelectionChange(Array.from(merged.values()));
  };

  const [emailColumn, ...restColumns] = columns;
  const totalColumns = columns.length + 2;

  return (
    <TableContainer sx={{ overflowX: "visible", overflowY: "visible", pb: 2 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell
              padding="checkbox"
              sx={{
                borderTop: "1px solid",
                borderColor: "#EAECF0",
              }}
            >
              <Checkbox
                id="users-table-select-all-checkbox"
                indeterminate={someSelected}
                checked={allSelected}
                onChange={(event) => {
                  event.stopPropagation();
                  toggleAll();
                }}
              />
            </TableCell>
            <TableCell
              width={235}
              sx={{
                borderTop: "1px solid",
                borderColor: "#EAECF0",
                p: 0,
              }}
            >
              <TableSortLabel
                active
                direction={orderBy === emailColumn.id ? sortOrder : "asc"}
                onClick={() => onToggleSort(emailColumn.id)}
                sx={{
                  "&.MuiTableSortLabel-root": {
                    padding: 0,
                    color: "black",
                  },
                  "& .MuiTableSortLabel-icon": {
                    marginLeft: "4px",
                    color: `${"black"} !important`,
                  },
                  "&:hover": {
                    textDecoration: "none",
                  },
                  "&.Mui-focusVisible": {
                    outline: "none",
                  },
                }}
              >
                {emailColumn.label}
              </TableSortLabel>
            </TableCell>
            <TableCell
              sx={{
                borderTop: "1px solid",
                borderColor: "#EAECF0",
                width: 130,
                paddingLeft: 1,
                paddingRight: 1,
              }}
            />
            {restColumns.map((c) => (
              <TableCell
                key={c.id}
                sx={{
                  borderTop: "1px solid",
                  borderColor: "#EAECF0",
                }}
              >
                <TableSortLabel
                  active
                  direction={orderBy === c.id ? sortOrder : "asc"}
                  onClick={() => onToggleSort(c.id)}
                  sx={{
                    "&.MuiTableSortLabel-root": {
                      padding: 0,
                      color: "black",
                    },
                    "& .MuiTableSortLabel-icon": {
                      marginLeft: "4px",
                      color: `${"black"} !important`,
                    },
                    "&:hover": {
                      textDecoration: "none",
                    },
                    "&.Mui-focusVisible": {
                      outline: "none",
                    },
                  }}
                >
                  {c.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {users.length === 0 ? (
            <TableRow>
              <TableCell colSpan={totalColumns} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  {isFiltered
                    ? "No users match the selected filters."
                    : "No users found."}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            users.map((user, index) => {
              const selected = isSelected(user);
              const pending = isPending(user);
              return (
                <TableRow
                  key={user.UserPortalUid}
                  hover={!pending}
                  onClick={() => toggleRow(user)}
                  sx={{
                    backgroundColor: index % 2 !== 0 ? "white" : gray[50],
                    borderBottom: "1px solid",
                    borderColor: "#EAECF0",
                    ...(pending && { cursor: "default" }),
                  }}
                >
                  <TableCell
                    padding="checkbox"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={selected}
                      disabled={pending}
                      onChange={(event) => {
                        event.stopPropagation();
                        toggleRow(user);
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      p: 0,
                    }}
                  >
                    {pending ? (
                      <Typography
                        variant="body2"
                        sx={{ color: "text.disabled", px: 1 }}
                      >
                        {user.email}
                      </Typography>
                    ) : (
                      <Link
                        to={`${linkTo}?userPortalUid=${encodeURIComponent(user.UserPortalUid)}`}
                        state={{ UserPortalUid: user.UserPortalUid }}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {user.email}
                      </Link>
                    )}
                  </TableCell>
                  <TableCell
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      width: 130,
                      textAlign: "center",
                      verticalAlign: "middle",
                      paddingLeft: 1,
                      paddingRight: 1,
                    }}
                  >
                    {user.portals && user.portals.length > 0 ? (
                      <MultiPortalTooltipContent portals={user.portals!} />
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Typography>{user.firstName}</Typography>
                  </TableCell>
                  <TableCell>{user.lastName}</TableCell>

                  <TableCell>{user.status}</TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
